import { useEffect, useState, useCallback, useRef } from "react";
import { Trans, useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import api, { apiErr, fmtClock, thumbnailCostSeconds } from "../lib/api";
import { connectYoutubePopup } from "../lib/youtubeOauth";
import { Card, btnPrimary, Alert, CostBadge } from "./ui-bits";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "./ui/tooltip";
import MyFolder from "./broll/MyFolder";
import ShortsCard from "./ShortsCard";
import RetentionPanel from "./RetentionPanel";
import { useUploads } from "../context/UploadContext";
import { toast } from "sonner";
import { Wand2, Loader2, CheckCircle2, AlertTriangle, Sparkles, Mic, Image as ImageIcon, RefreshCw, PlayCircle, ArrowLeft, Video, Play, Pause, Check, Clock, Circle, Layers, Youtube, Upload, Unplug, CalendarClock, Copy, Download, FileText, Mail, PenLine, X, Eye, ThumbsUp, MessageCircle, UserPlus, Timer, PieChart } from "lucide-react";

const fmtCompact = (n) => new Intl.NumberFormat(undefined, { notation: "compact" }).format(n || 0);
const fmtWatchTime = (minutes) => {
  const m = minutes || 0;
  return m >= 60 ? `${(m / 60).toFixed(1)}h` : `${Math.round(m)} min`;
};

// Aviso sonoro de fin de pipeline: un acorde ascendente breve generado con Web Audio API (sin
// archivo de audio que alojar/licenciar). Se usa para awaiting_approval, render completo, y los
// dos estados de error — cualquier punto en el que el usuario deja de ser un mero espectador y
// necesita volver a mirar la pantalla si se ha ido a hacer otra cosa.
function playChime() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const notes = [523.25, 659.25, 783.99]; // Do5-Mi5-Sol5, tríada ascendente ("listo" alegre)
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      const start = ctx.currentTime + i * 0.12;
      gain.gain.setValueAtTime(0, start);
      gain.gain.linearRampToValueAtTime(0.2, start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, start + 0.35);
      osc.connect(gain).connect(ctx.destination);
      osc.start(start);
      osc.stop(start + 0.4);
    });
    setTimeout(() => { ctx.close().catch(() => {}); }, (notes.length * 0.12 + 0.4) * 1000 + 100);
  } catch (e) { /* silencioso — el aviso por email sigue llegando igual */ }
}

const fmtElapsed = (sec) => {
  sec = Math.max(0, Math.round(sec || 0));
  const h = Math.floor(sec / 3600), m = Math.floor((sec % 3600) / 60), s = sec % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
};

/**
 * Pantalla de producción "Crear vídeo en 1 clic" — hace polling del pipeline, muestra progreso
 * y (cuando llega a `awaiting_approval`) presenta el preview con thumbnails para que el
 * usuario apruebe o rechace el B-roll elegido por la IA.
 *
 * Estados del backend (`one_click.status`):
 *   "en_curso" → pipeline corriendo (step: script | narration | media | composing)
 *   "awaiting_approval" → pausa para preview (mostrar thumbnails + botones)
 *   "rendering" → render en curso (progreso propio dentro del mismo riel, ver RenderStage)
 *   "error" → muestra mensaje accionable + botón volver
 *
 * DISEÑO (cadena de montaje): mientras el pipeline trabaja solo (en_curso o rendering), un
 * RIEL de 5 estaciones (guion→voz→material→composición→render) sustituye a las 4 tarjetas
 * estáticas de antes. Debajo, un ÚNICO viewport narra en tiempo real qué pasa en la estación
 * activa (con datos reales, no un spinner mudo) y hace fundido cruzado al pasar de una a otra.
 * Los puntos de DECISIÓN (aprobar preview) o de PROBLEMA (error de guion/voz/material, o de
 * render) tienen su propia pantalla dedicada, fuera del riel — son momentos distintos de "la
 * IA está trabajando".
 */
const STAGE_KEYS = ["script", "narration", "media", "composing", "render"];
const STAGE_ICONS = { script: Sparkles, narration: Mic, media: ImageIcon, composing: Layers, render: Wand2 };

// Etiquetas legibles para el badge de fuente de cada clip
const SOURCE_LABELS = {
  wikimedia: "Wikimedia", internet_archive: "Internet Archive", archive: "Internet Archive",
  unsplash: "Unsplash", pexels: "Pexels", pixabay: "Pixabay", met: "Met Museum", smithsonian: "Smithsonian",
  wellcome: "Wellcome Collection",
  coverr: "Coverr", openverse: "Openverse", ia: "IA generada",
  web: "Web abierta", grafico: "Escena gráfica",
};
const SOURCE_COLORS = {
  wikimedia: "bg-emerald-100 text-emerald-800",
  internet_archive: "bg-emerald-100 text-emerald-800",
  archive: "bg-emerald-100 text-emerald-800",
  unsplash: "bg-slate-900 text-white",
  pexels: "bg-teal-100 text-teal-800",
  pixabay: "bg-sky-100 text-sky-800",
  met: "bg-rose-100 text-rose-800",
  smithsonian: "bg-orange-100 text-orange-800",
  wellcome: "bg-cyan-100 text-cyan-800",
  coverr: "bg-indigo-100 text-indigo-800",
  openverse: "bg-amber-100 text-amber-800",
  ia: "bg-gradient-to-br from-fuchsia-500 to-purple-600 text-white",
  web: "bg-amber-500 text-white",
  grafico: "bg-gradient-to-br from-amber-400 to-yellow-600 text-black",
};
// Fuentes que el buscador manual de Mi carpeta sabe filtrar de verdad (ver IMAGE_SOURCES/
// VIDEO_SOURCES en PexelsBrowser.jsx) — "ia" y "grafico" no son buscables (son un fallback de
// generación y una tarjeta de texto, no un banco con el que consultar), así que para esos dos
// no hay fuente que preseleccionar y se deja "todas".
const _IMG_SEARCH_SOURCES = new Set(["pexels", "pixabay", "unsplash", "openverse", "wikimedia", "met", "smithsonian", "wellcome", "archive"]);
const _VID_SEARCH_SOURCES = new Set(["pexels", "pixabay", "archive"]);
// Al pulsar "reemplazar" sobre un clip, la búsqueda debe arrancar ya apuntando a la MISMA
// pestaña y fuente de las que vino ese clip — no tiene sentido ofrecer "vídeo, todas las
// fuentes" por defecto cuando el propio clip ya dice de dónde salió (director_source).
function replaceSearchParams(clip) {
  const media = clip?.media === "image" ? "image" : "video";
  const src = clip?.director_source;
  // "web" es su propia pestaña (el buscador la llama internamente "character", ver
  // PexelsBrowser.jsx) y no tiene desplegable de fuente propio.
  if (src === "web") return { media: "character", source: "" };
  const valid = media === "image" ? _IMG_SEARCH_SOURCES : _VID_SEARCH_SOURCES;
  return { media, source: valid.has(src) ? src : "" };
}

// ── Riel de estaciones ────────────────────────────────────────────────────────────────────
// Tira oscura con textura de "perforación de cine" (los puntitos arriba/abajo) — el motivo
// visual que amarra todo el componente a la identidad cinematográfica del resto de la app.
function AssemblyRail({ stages, activeIdx }) {
  const pct = stages.length > 1 ? (activeIdx / (stages.length - 1)) * 100 : 0;
  return (
    <div className="relative overflow-hidden rounded-2xl bg-slate-900 px-6 pt-5 pb-4" data-testid="oc-rail">
      <div className="pointer-events-none absolute inset-x-0 top-1.5 flex justify-between px-4 opacity-60">
        {Array.from({ length: 24 }).map((_, i) => <span key={i} className="h-1 w-1 rounded-full bg-white/10" />)}
      </div>
      <div className="pointer-events-none absolute inset-x-0 bottom-1.5 flex justify-between px-4 opacity-60">
        {Array.from({ length: 24 }).map((_, i) => <span key={i} className="h-1 w-1 rounded-full bg-white/10" />)}
      </div>
      <div className="relative flex items-start justify-between">
        <div className="absolute left-0 right-0 top-[19px] h-[3px] rounded-full bg-white/10" />
        <div
          className="absolute left-0 top-[19px] h-[3px] rounded-full bg-gradient-to-r from-amber-400 to-orange-500 transition-all duration-700 ease-out"
          style={{ width: `${pct}%` }}
        />
        {stages.map((s, i) => {
          const done = i < activeIdx;
          const active = i === activeIdx;
          return (
            <div key={s.key} className="relative z-10 flex flex-1 flex-col items-center gap-2">
              <div className={`flex h-10 w-10 items-center justify-center rounded-full border-2 transition-all duration-500 ${
                done ? "border-amber-400 bg-gradient-to-br from-amber-400 to-orange-500 text-white shadow-lg shadow-amber-500/30"
                : active ? "border-amber-400 bg-slate-900 text-amber-300 shadow-md shadow-amber-500/20"
                : "border-white/15 bg-slate-900 text-white/30"}`}>
                {active && (
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400/30" />
                )}
                {done ? <Check size={18} strokeWidth={3} className="relative" />
                  : active ? <Loader2 size={18} className="relative animate-spin" />
                  : <s.icon size={16} className="relative" />}
              </div>
              <span className={`text-center text-[11px] font-bold uppercase tracking-wide leading-tight ${
                done ? "text-amber-300" : active ? "text-white" : "text-white/30"}`}>
                {s.label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Cuerpo — Fase Guion ───────────────────────────────────────────────────────────────────
// Sin datos parciales reales (el LLM devuelve el guion de golpe): la elegancia viene del
// movimiento — mensajes que se relevan, un cursor que respira, un cronómetro real.
function ScriptStage({ t, msgIdx, scriptMsgs, elapsedSec }) {
  return (
    <Card className="relative overflow-hidden !border-slate-800 !bg-slate-900 p-10 text-center" data-testid="oc-stage-script">
      <div className="pointer-events-none absolute -top-24 left-1/2 h-64 w-[36rem] -translate-x-1/2 rounded-full bg-amber-500/10 blur-3xl" />
      <div className="relative">
        <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full border border-amber-400/30 bg-amber-400/10">
          <Sparkles size={26} className="animate-pulse text-amber-400" />
        </div>
        <div className="mb-3 text-[11px] font-bold uppercase tracking-[0.35em] text-amber-400/80">{t("oc.writingBadge")}</div>
        <AnimatePresence mode="wait">
          <motion.div key={msgIdx % scriptMsgs.length}
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.5 }}
            className="flex items-center justify-center gap-2 text-xl font-semibold text-slate-100">
            <span>{scriptMsgs[msgIdx % scriptMsgs.length]}</span>
          </motion.div>
        </AnimatePresence>
        <div className="mt-5 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3.5 py-1.5 text-sm text-slate-400">
          <Clock size={14} /> <span className="tabular-nums">{fmtClock(elapsedSec)}</span>
          <span className="text-slate-500">· {t("oc.scriptPhaseEta")}</span>
        </div>
      </div>
    </Card>
  );
}

// ── Cuerpo — Fase Voz ─────────────────────────────────────────────────────────────────────
// Consola de grabación: una fila por escena, con "vúmetro" animado en la que se está grabando
// ahora mismo y botón de preescucha real en cuanto una escena termina.
function NarrationStage({ t, segments, playingSeg, playSeg, activeIdx }) {
  const done = segments.filter((s) => s.narration_status === "completo").length;
  return (
    <Card className="overflow-hidden p-0" data-testid="oc-stage-narration">
      <div className="flex items-center justify-between gap-3 bg-slate-900 px-6 py-4">
        <div className="flex items-center gap-2.5">
          <Mic size={18} className="text-amber-400" />
          <span className="font-bold text-white">{t("oc.narrationHeadline")}</span>
        </div>
        <span className="rounded-full border border-amber-400/30 bg-amber-400/10 px-2.5 py-1 text-xs font-bold tabular-nums text-amber-300">
          {t("oc.narrationProgress", { done, total: segments.length })}
        </span>
      </div>
      <div className="max-h-[26rem] divide-y divide-slate-100 overflow-y-auto">
        {segments.map((seg, idx) => {
          const narrOk = seg.narration_status === "completo";
          const narrGen = seg.narration_status === "generando";
          const audioSec = Math.round(seg.narration_duration || seg.duration || 0);
          const active = idx === activeIdx;
          return (
            <div key={seg.id}
              className={`flex items-center gap-4 px-6 py-3.5 transition-colors duration-500 ${active ? "bg-amber-50/70" : "bg-white"}`}
              data-testid={`oc-narr-scene-${seg.num}`}>
              <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold ${
                narrOk ? "bg-gradient-to-br from-amber-400 to-orange-500 text-white shadow-md"
                : active ? "border-2 border-amber-400 bg-white text-amber-600" : "bg-slate-100 text-slate-500"}`}>
                {narrOk ? <Check size={15} strokeWidth={3} /> : seg.num}
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate font-semibold text-slate-900">{seg.title}</div>
                {narrGen ? (
                  <div className="mt-1 flex items-end gap-0.5" data-testid={`oc-waveform-${seg.num}`}>
                    {[6, 12, 8, 16, 10, 14, 7].map((h, i) => (
                      <span key={i} className="w-1 animate-pulse rounded-full bg-amber-400"
                        style={{ height: `${h}px`, animationDelay: `${i * 90}ms`, animationDuration: "700ms" }} />
                    ))}
                    <span className="ml-2 text-xs text-amber-700">{t("oc.recordingVoice")}</span>
                  </div>
                ) : narrOk ? (
                  <div className="flex items-center gap-1.5 text-xs text-slate-500">
                    <Mic size={11} className="text-emerald-600" /> {t("oc.narratedSeconds", { count: audioSec })}
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5 text-xs text-slate-400"><Clock size={11} /> {t("oc.queued")}</div>
                )}
              </div>
              {narrOk && seg.audio_url && (
                <button
                  onClick={() => playSeg(seg)}
                  title={t("oc.listenNarration")}
                  data-testid={`oc-play-${seg.num}`}
                  className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-2 transition-all ${
                    playingSeg === seg.id ? "border-amber-500 bg-amber-500 text-white shadow-md"
                    : "border-slate-200 text-slate-500 hover:border-amber-400 hover:text-amber-600"}`}>
                  {playingSeg === seg.id ? <Pause size={15} /> : <Play size={15} className="ml-0.5" />}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}

// ── Cuerpo — Fase Material ────────────────────────────────────────────────────────────────
// Hoja de contactos: los clips aterrizan en una tira horizontal según se descargan, con su
// fuente y su encargo (director_query) — el director de cine eligiendo material ante tus ojos.
// Nº de huecos fijos del "tablero" de material — sin scroll: en vez de acumular un filmstrip
// que hay que desplazar, se muestra siempre esta cantidad fija de tarjetas. Cuando entra un
// clip nuevo, el más antiguo desaparece (AnimatePresence popLayout) — la sensación es de cinta
// transportadora viva, no de lista que crece sin parar.
const MEDIA_SLOTS = 10; // 2 filas de 5 (grid sm:grid-cols-5)

// Igual que ClipThumbnail (ver más abajo) pero para el filmstrip en vivo de MediaStage: si
// c.thumbnail existe pero la imagen no llega a cargar, antes se ocultaba el <img> vía estilo
// inline sin fallback, dejando el hueco en blanco — mismo bug, mismo arreglo (useState en vez
// de mutar el DOM directamente).
function FilmstripThumb({ c }) {
  const [failed, setFailed] = useState(false);
  if (c.thumbnail && !failed) {
    return (
      <img src={c.thumbnail} alt="" loading="lazy" className="aspect-video w-full bg-slate-100 object-cover"
        onError={() => setFailed(true)} />
    );
  }
  return (
    <div className="flex aspect-video w-full items-center justify-center bg-slate-900">
      <Sparkles size={16} className="text-amber-400" />
    </div>
  );
}

// Tarjeta "vacía" que ocupa el hueco del PRÓXIMO clip mientras se busca (jul 2026) — antes,
// entre un clip encontrado y el siguiente, la pantalla no decía nada durante varios segundos
// (una query difícil de encontrar podía sentirse congelada). Muestra en vivo el `step_detail`
// real que manda el backend ("Buscando «...» en Wikimedia…", ver _auto_fill_broll_v2), no un
// spinner mudo — mismo tamaño/forma que un FilmstripThumb ya resuelto, para que el usuario vea
// literalmente "aquí va a aparecer el siguiente".
// Sale girando sobre el eje Y (en vez del scale+fade genérico del resto de tarjetas) para que,
// combinada con la entrada girada de FoundCard (mismo hueco de la rejilla, ver más abajo), el
// conjunto lea como "la propia tarjeta se da la vuelta" al encontrar el material — no una
// tarjeta que desaparece y otra distinta que aparece al lado.
function CurrentSearchCard({ text }) {
  return (
    <motion.div key="__current_search__" layout="position"
      initial={{ opacity: 0, scale: 0.6 }}
      animate={{ opacity: 1, scale: 1, rotateY: 0 }}
      exit={{ opacity: 0, rotateY: -100 }}
      transition={{ type: "spring", stiffness: 420, damping: 28 }}
      style={{ transformStyle: "preserve-3d", backfaceVisibility: "hidden" }}
      className="relative flex aspect-video w-full flex-col items-center justify-center gap-1.5 overflow-hidden rounded-lg border-2 border-dashed border-amber-300 bg-amber-50 px-2 text-center"
      data-testid="oc-current-search-card">
      <Loader2 size={16} className="animate-spin text-amber-500" />
      <span className="line-clamp-3 text-[10px] font-medium leading-tight text-amber-800">{text}</span>
    </motion.div>
  );
}

// El clip RECIÉN encontrado (recent[0] — ver MediaStage) entra con un giro sobre el eje Y en
// vez del scale+fade genérico: como CurrentSearchCard SALE girando en el mismo hueco de la
// rejilla justo antes, el efecto combinado se lee como una única tarjeta que se da la vuelta
// para revelar la imagen, no dos tarjetas distintas apareciendo/desapareciendo por separado.
// El resto de tarjetas (ya asentadas) siguen con la animación de reordenación normal.
function FoundCard({ c, sourceLabel, isNewest }) {
  return (
    <motion.div key={c.id} layout="position"
      initial={isNewest ? { opacity: 0, rotateY: 100 } : { opacity: 0, scale: 0.6 }}
      animate={{ opacity: 1, scale: 1, rotateY: 0 }}
      exit={{ opacity: 0, scale: 0.6 }}
      transition={isNewest
        ? { type: "spring", stiffness: 260, damping: 24 }
        : { type: "spring", stiffness: 420, damping: 28 }}
      style={{ transformStyle: "preserve-3d", backfaceVisibility: "hidden" }}
      className="relative overflow-hidden rounded-lg border-2 border-slate-200">
      <FilmstripThumb c={c} />
      <div className={`absolute left-1 top-1 rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide ${SOURCE_COLORS[c.director_source] || "bg-slate-100 text-slate-700"}`}>
        {sourceLabel(c.director_source || "?")}
      </div>
      {c.director_query && (
        <div className="truncate bg-white px-1.5 py-1 text-[10px] text-slate-600" title={c.director_query}>{c.director_query}</div>
      )}
    </motion.div>
  );
}

function MediaStage({ t, sourceLabel, clips, clipsByScene, activeIdx, stepDetail }) {
  // Recuento por fuente (en vez de un total agregado "N clips de M fuentes") — así se ve de un
  // vistazo de dónde viene el material, no solo cuánto hay.
  const countsBySource = clips.reduce((acc, c) => {
    const src = c.director_source || "?";
    acc[src] = (acc[src] || 0) + 1;
    return acc;
  }, {});
  const sourceEntries = Object.entries(countsBySource).sort((a, b) => b[1] - a[1]);
  // Un hueco menos cuando se muestra la tarjeta "buscando…" (mantiene la rejilla en 2 filas de 5).
  const recent = clips.slice(-(stepDetail ? MEDIA_SLOTS - 1 : MEDIA_SLOTS)).reverse();
  return (
    <Card className="overflow-hidden p-0" data-testid="oc-stage-media">
      <div className="flex flex-wrap items-center justify-between gap-2 bg-slate-900 px-6 py-4">
        <div className="flex items-center gap-2.5">
          <ImageIcon size={18} className="text-amber-400" />
          <span className="font-bold text-white">{t("oc.mediaHeadline")}</span>
        </div>
        <div className="flex flex-wrap items-center gap-1.5" data-testid="oc-media-source-counts">
          {sourceEntries.map(([src, count]) => (
            <span key={src} className={`rounded-full px-2.5 py-1 text-xs font-bold ${SOURCE_COLORS[src] || "bg-slate-100 text-slate-700"}`}>
              {sourceLabel(src)} · {count}
            </span>
          ))}
        </div>
      </div>
      <div className="p-5">
        {recent.length > 0 || stepDetail ? (
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2.5" style={{ perspective: 1000 }} data-testid="oc-filmstrip">
            <AnimatePresence mode="popLayout" initial={false}>
              {stepDetail && <CurrentSearchCard key="__current_search__" text={stepDetail} />}
              {recent.map((c, i) => (
                <FoundCard key={c.id} c={c} sourceLabel={sourceLabel} isNewest={i === 0} />
              ))}
            </AnimatePresence>
          </div>
        ) : (
          <div className="flex items-center gap-2 py-3 text-sm text-slate-500">
            <Loader2 size={16} className="animate-spin text-amber-500" /> {t("oc.mediaSearching")}
          </div>
        )}
        <div className="mt-4 space-y-1.5" data-testid="oc-media-scenes">
          {clipsByScene.map(({ seg, clips: segClips }, idx) => {
            const au = seg.narration_duration || seg.duration || 0;
            const covered = segClips.reduce((x, c) => x + (c.duration || 0), 0);
            const filled = au > 0 && covered >= au - 3.5; // tolerancia = GRAPHIC_FILL_MIN_GAP del backend
            const active = idx === activeIdx;
            return (
              <div key={seg.id} className={`flex items-center gap-3 rounded-lg px-3 py-1.5 text-sm ${active ? "bg-amber-50" : ""}`}>
                <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold ${
                  filled ? "bg-emerald-100 text-emerald-700" : active ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-400"}`}>
                  {filled ? <Check size={11} strokeWidth={3} /> : seg.num}
                </span>
                <span className="min-w-0 flex-1 truncate text-slate-700">{seg.title}</span>
                <span className="shrink-0 text-xs font-semibold text-slate-400">{t("oc.clipsCount", { count: segClips.length })}</span>
              </div>
            );
          })}
        </div>
      </div>
    </Card>
  );
}

// ── Cuerpo — Fase Composición ─────────────────────────────────────────────────────────────
// Igual que ScriptStage: sin datos parciales reales por plano, así que la elegancia viene del
// movimiento — mensajes que se relevan (oc.composingMsgs), mismo msgIdx/temporizador que ya
// rota los de guion (ver el useEffect de "rot" en el padre).
function ComposingStage({ t, msgIdx, composingMsgs, musicMood }) {
  return (
    <Card className="relative overflow-hidden !border-slate-800 !bg-slate-900 p-10 text-center" data-testid="oc-stage-composing">
      <div className="pointer-events-none absolute -top-24 left-1/2 h-64 w-[36rem] -translate-x-1/2 rounded-full bg-amber-500/10 blur-3xl" />
      <div className="relative">
        <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center justify-self-center rounded-full border border-amber-400/30 bg-amber-400/10">
          <Layers size={26} className="animate-pulse text-amber-400" />
        </div>
        <div className="mb-3 text-[11px] font-bold uppercase tracking-[0.35em] text-amber-400/80">{t("oc.composingBadge")}</div>
        <AnimatePresence mode="wait">
          <motion.div key={msgIdx % composingMsgs.length}
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.5 }}
            className="text-xl font-semibold text-slate-100">
            {composingMsgs[msgIdx % composingMsgs.length]}
          </motion.div>
        </AnimatePresence>
        {musicMood && (
          <div className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-amber-400/30 bg-amber-400/10 px-3.5 py-1.5 text-sm font-semibold text-amber-300">
            🎵 {musicMood}
          </div>
        )}
      </div>
    </Card>
  );
}

// Texto ilustrativo REAL por paso (qué hace técnicamente cada uno) — se muestra como
// respaldo cuando no hay detail_items en ese instante (p.ej. durante los pasos de un solo
// pase como subtítulos/exportar/comprimir, donde no hay "escenas en paralelo" que enseñar).
// Nunca es relleno genérico: describe lo que _finalize_render hace de verdad en ese paso.
const RENDER_STEP_BODY_KEYS = ["prep", "broll", "narr", "compose", "subs", "export", "compress"];

// Miniatura pequeña (pila de escena) dentro de la tarjeta de actividad de render, con el mismo
// fallback por estado que ClipThumbnail/FilmstripThumb — si la imagen no carga, desaparece del
// apilado en vez de dejar un hueco roto (aquí no hace falta placeholder de texto: es solo
// ambientación, hay otras miniaturas al lado y el texto de estado ya está debajo).
function ScanThumb({ url, style }) {
  const [failed, setFailed] = useState(false);
  if (!url || failed) return null;
  return (
    <img src={url} alt="" className="absolute inset-0 h-full w-full object-cover" style={style}
      onError={() => setFailed(true)} />
  );
}

// ── Cuerpo — Fase Render ──────────────────────────────────────────────────────────────────
// La fase final y más larga: progreso real del tracker global (mismo origen que la pestaña
// clásica de render — progreso fino, actividad por escena en paralelo, cronómetro). Los pasos
// ya completados NO se repiten aquí (ya están marcados arriba en el riel de estaciones); el
// foco visual es el paso ACTIVO con su información real.
function RenderStage({ t, liveRender, clipsByScene }) {
  // Solo corre un render a la vez en todo el servidor (ver _enqueue_render en el backend): si
  // había otro en marcha, este queda "en_cola" y el progreso real todavía no existe. Sin esta
  // rama la fase de render se pintaba vacía (0%, sin actividad, sin pasos), dando la sensación
  // de que se había colgado — la vista manual sí lo avisaba (ver TabRenderizar) y esta no.
  if (liveRender.status === "en_cola") {
    return (
      <Card className="p-8 sm:p-10" data-testid="oc-stage-render-queued">
        <div className="mx-auto flex max-w-md flex-col items-center text-center">
          <div className="relative mb-5 flex h-24 w-24 shrink-0 items-center justify-center">
            <div className="absolute inset-0 rounded-full border-2 border-dashed border-amber-200 animate-spin" style={{ animationDuration: "12s" }} />
            <div className="flex h-10 w-14 items-center justify-center rounded-lg border-2 border-amber-200 bg-gradient-to-br from-amber-50 to-orange-100 shadow-sm">
              <Video size={20} className="text-amber-500" />
            </div>
          </div>
          <h3 className="mb-1.5 text-xl font-bold text-slate-900">{t("render.queuedTitle")}</h3>
          <p className="text-base text-slate-500">{t("render.queuedNote")}</p>
          <div className="mt-5 w-full border-t border-slate-100 pt-4">
            <p className="text-sm text-slate-400">{t("render.queuedFooter")}</p>
          </div>
        </div>
      </Card>
    );
  }
  const steps = liveRender.steps || [];
  const activeStepIdx = steps.findIndex((s) => s.status === "en_proceso");
  const pendingSteps = steps.filter((s) => s.status === "pendiente");
  const activeStep = activeStepIdx >= 0 ? steps[activeStepIdx] : null;
  const hasActivity = liveRender.detail_items?.length > 0;
  // Miniaturas REALES del material ya elegido para cada escena (fase de material, ya en
  // project.broll.clips) — así "Componer vídeo" enseña EXACTAMENTE lo que se está uniendo
  // ahora mismo en vez de solo un punto parpadeando junto a texto de estado.
  const scenePreview = {};
  (clipsByScene || []).forEach(({ seg, clips: segClips }) => {
    scenePreview[String(seg.num)] = (segClips || []).filter((c) => c.thumbnail).slice(0, 8);
  });
  return (
    <Card className="overflow-hidden p-0" data-testid="oc-stage-render">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 bg-slate-900 px-6 py-4">
        <div className="flex items-center gap-2.5">
          <Wand2 size={18} className="text-amber-400" />
          <span className="font-bold text-white">{t("oc.renderingTitle")}</span>
        </div>
        <div className="ml-auto flex items-center gap-2">
          {liveRender.elapsed_seconds != null && (
            <span className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-xs font-semibold tabular-nums text-slate-400">
              <Clock size={11} /> {t("render.elapsed", { time: fmtElapsed(liveRender.elapsed_seconds) })}
            </span>
          )}
          <span className="rounded-full border border-amber-400/30 bg-amber-400/10 px-2.5 py-1 text-xs font-bold tabular-nums text-amber-300">{liveRender.progress || 0}%</span>
        </div>
      </div>
      <div className="p-6">
        <div className="mb-5 h-2.5 w-full overflow-hidden rounded-full bg-slate-200">
          <div className="h-full rounded-full bg-gradient-to-r from-amber-400 to-orange-500 transition-all duration-700"
            style={{ width: `${liveRender.progress || 0}%` }} />
        </div>

        {/* Paso activo: foco visual principal, con la info REAL más rica disponible. Los pasos
            YA hechos no se repiten aquí: el riel de estaciones de arriba ya los marca como
            completos, así que una fila/línea extra por cada uno era pura redundancia. */}
        {activeStep && (
          <div className="mb-4 rounded-xl border-2 border-amber-300 bg-amber-50/60 p-4" data-testid="oc-render-active-step">
            <div className="mb-2 flex items-center gap-2.5">
              <Loader2 size={18} className="shrink-0 animate-spin text-amber-500" />
              <span className="font-bold text-amber-900">{activeStep.name}</span>
            </div>
            {hasActivity ? (
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2" data-testid="oc-rendering-activity">
                {liveRender.detail_items.map((item, i) => {
                  const isGlobal = item.key.startsWith("_global");
                  const thumbs = isGlobal ? [] : (scenePreview[String(item.key)] || []);
                  return (
                    <div key={i} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-3 rounded-lg border border-amber-200 bg-white px-3 py-2 overflow-hidden">
                      <div className="min-w-0 shrink-0">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-amber-600">
                          {isGlobal ? t("render.globalStep") : t("render.sceneLabel", { num: item.key })}
                        </div>
                        <div className="text-sm font-semibold leading-snug text-slate-800">{item.text}</div>
                      </div>
                      {thumbs.length > 0 ? (
                        <div className="relative h-9 shrink-0 overflow-hidden rounded-md" style={{ width: `${24 + thumbs.length * 14}px` }}>
                          {thumbs.map((c, ci) => (
                            <div key={c.id || ci}
                              className="absolute top-0 h-9 w-9 overflow-hidden rounded-md border-2 border-white shadow-sm"
                              style={{ left: `${ci * 14}px`, zIndex: thumbs.length - ci }}>
                              <ScanThumb url={c.thumbnail} />
                            </div>
                          ))}
                          <span className="pointer-events-none absolute inset-y-0 left-0 w-4 animate-render-scan bg-gradient-to-r from-transparent via-white/80 to-transparent"
                            style={{ zIndex: thumbs.length + 1 }} />
                        </div>
                      ) : (
                        <span className="relative mt-0 flex h-2 w-2 shrink-0">
                          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400 opacity-60" />
                          <span className="relative inline-flex h-2 w-2 rounded-full bg-amber-500" />
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-sm text-amber-800">
                {t(`oc.renderStep.${RENDER_STEP_BODY_KEYS[activeStepIdx] || "compose"}`)}
              </div>
            )}
          </div>
        )}

        {/* Lo que queda por delante — discreto, para no repetir el riel pero sin perder la
            sensación de cadena completa. */}
        {pendingSteps.length > 0 && (
          <div className="flex items-center gap-2 text-xs text-slate-400" data-testid="oc-render-pending-summary">
            <Circle size={12} className="shrink-0" />
            <span className="truncate">{t("oc.renderUpNext", { steps: pendingSteps.map((s) => s.name).join(" · ") })}</span>
          </div>
        )}
      </div>
    </Card>
  );
}

// Miniatura de un clip en la revisión de material, con su propio estado de error: si
// c.thumbnail existe pero la imagen no llega a cargar (storage caído, URL caducada...), antes
// se ocultaba el <img> vía estilo inline sin ningún fallback visible, dejando la tarjeta en
// blanco (solo el badge de fuente, sin icono ni texto) — bug real reportado en producción.
// Necesita su propio componente (no un simple onError inline) porque hace falta un useState
// para "olvidar" el <img> roto y pasar a renderizar el mismo placeholder que ya existe para
// cuando no hay thumbnail_url en absoluto.
function ClipThumbnail({ c, t, playing, onTogglePreview, onReplace }) {
  const [failed, setFailed] = useState(false);
  const videoRef = useRef(null);
  // Antes se reproducía automáticamente al pasar el ratón (hover) — quitado (jul 2026): frágil
  // (intermitente, interferido por el overlay de "reemplazar" encima en el stacking) y el
  // usuario lo notó recargado. Ahora es un botón EXPLÍCITO (ver el <button> de play/pause más
  // abajo) que solo reproduce/pausa cuando se pulsa — mismo `playing` para todos los
  // ClipThumbnail (gestionado por el padre, un único clip reproduce a la vez).
  // Lista BLANCA (no negra): antes excluía solo "graphic"/"image", así que los tipos nuevos
  // "destacada"/"personaje"/"objeto" (fotos, no vídeos — ver server.py) colaban por aquí e
  // intentaban reproducir un archivo de imagen como <video>, que no carga nada — bug real
  // reportado ("no veo que se reproduzca el video" al pasar por encima del material).
  const isVideo = c.media === "video" && !!c.video_url;
  const onTime = () => {
    const v = videoRef.current;
    if (!v) return;
    if (c.end != null && v.currentTime >= c.end) {
      v.currentTime = c.start != null ? c.start : 0;  // bucle dentro del corte
    }
  };

  if (c.media === "graphic") {
    const texto = c.graphic?.texto || c.director_query || c.title || "";
    return (
      <div className="relative w-full aspect-video bg-amber-50 flex flex-col items-center justify-center gap-1.5 px-3 text-center overflow-hidden">
        <FileText size={16} className="text-amber-400 shrink-0" />
        {texto && (
          <span className="text-xs font-bold text-amber-900 leading-snug line-clamp-3">{texto}</span>
        )}
        <span className="text-[8px] font-medium text-amber-600">{t("oc.graphicNoPreview")}</span>
        <button type="button"
          data-testid={`oc-clip-replace-${c.id}`}
          onClick={(e) => { e.stopPropagation(); onReplace(c); }}
          title={t("oc.clickToReplace")}
          className="absolute bottom-1.5 left-1.5 z-10 inline-flex items-center justify-center w-7 h-7 rounded-full bg-black/70 text-white hover:bg-black/90 transition-colors">
          <RefreshCw size={13} />
        </button>
        <div className="absolute bottom-1.5 right-1.5 px-1.5 py-0.5 rounded bg-black/70 text-white text-[10px] font-bold">
          {Math.round(Number(c.duration))}s
        </div>
      </div>
    );
  }
  return (
    <div className="relative w-full aspect-video bg-slate-100">
      {c.thumbnail && !failed
        ? <img src={c.thumbnail} alt={c.director_query || c.title} className="w-full h-full object-cover" onError={() => setFailed(true)} />
        : !isVideo && (
          <div className="w-full h-full flex items-center justify-center bg-slate-100">
            <Video size={30} className="text-slate-300" />
          </div>
        )}
      {isVideo && playing && (
        <video
          ref={videoRef}
          src={c.video_url}
          data-testid={`oc-video-preview-${c.id}`}
          autoPlay muted playsInline preload="metadata" loop={c.end == null}
          onLoadedData={() => { const v = videoRef.current; if (v && c.start != null) { try { v.currentTime = c.start; } catch (e) {} } }}
          onTimeUpdate={c.end != null ? onTime : undefined}
          className="absolute inset-0 w-full h-full object-cover"
        />
      )}
      {isVideo && (
        <button type="button"
          data-testid={`oc-clip-preview-toggle-${c.id}`}
          onClick={(e) => { e.stopPropagation(); onTogglePreview(c.id); }}
          title={t(playing ? "oc.stopPreview" : "oc.playPreview")}
          className="absolute bottom-1.5 left-1.5 z-10 inline-flex items-center justify-center w-7 h-7 rounded-full bg-black/70 text-white hover:bg-black/90 transition-colors">
          {playing ? <Pause size={14} /> : <Play size={14} />}
        </button>
      )}
      {/* "Reemplazar" vive AQUÍ (dentro del contenedor de la imagen), no en la tarjeta exterior
          — bug real reportado ("sigue viéndose mal"): posicionado en el contenedor exterior (que
          incluye el texto de la query debajo), su `bottom` se medía desde el borde de TODA la
          tarjeta, no desde el borde de la imagen, así que quedaba solapado con el texto. */}
      <button type="button"
        data-testid={`oc-clip-replace-${c.id}`}
        onClick={(e) => { e.stopPropagation(); onReplace(c); }}
        title={t("oc.clickToReplace")}
        className={`absolute bottom-1.5 z-10 inline-flex items-center justify-center w-7 h-7 rounded-full bg-black/70 text-white hover:bg-black/90 transition-colors ${isVideo ? "left-10" : "left-1.5"}`}>
        <RefreshCw size={13} />
      </button>
      {/* La duración se mueve aquí también, por el mismo motivo: en el contenedor exterior su
          posición se medía desde el borde de TODA la tarjeta (imagen + texto de abajo), no del
          borde real de la imagen — mismo tipo de desajuste, solo que menos visible en un badge
          pequeño que en un botón circular grande. */}
      <div className="absolute bottom-1.5 right-1.5 px-1.5 py-0.5 rounded bg-black/70 text-white text-[10px] font-bold">
        {Math.round(Number(c.duration))}s
      </div>
    </div>
  );
}

// ── Pantalla final — publicar en YouTube ─────────────────────────────────────────────────
// El 1-clic no termina en "vídeo renderizado": termina en "publicado" (o al menos listo para
// publicarse) — por eso esta pantalla vive DENTRO del propio componente en vez de reenviar a
// la pestaña clásica "Descarga" (TabDescarga.jsx), de la que reutiliza el flujo de
// conexión/subida a YouTube y las claves i18n `dl.*` (ya existían, cubren esto por completo).
// La miniatura suele venir ya generada en automático (`_auto_generate_thumbnail_for_one_click`
// en el backend), pero el usuario puede haber optado por no crearla o haber fallado la
// generación — por eso esta pantalla SÍ incluye los mismos controles de crear/regenerar/subir
// miniatura que TabThumbnail.jsx, no asume que ya existe.
function PublishStage({ t, project, out, projectId, onRecreate, onCreateAnother }) {
  const { startThumbnail, startYoutubeUpload } = useUploads();
  const [yt, setYt] = useState(null);
  const [copied, setCopied] = useState("");
  const [title, setTitle] = useState(project?.name || "");
  const [description, setDescription] = useState(out?.description || "");
  const [privacy, setPrivacy] = useState("private");
  const [schedule, setSchedule] = useState(false);
  const [publishAt, setPublishAt] = useState("");
  const [aiDisclosure, setAiDisclosure] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [upload, setUpload] = useState(null);
  const [ytStats, setYtStats] = useState(null);
  const [thumbBusy, setThumbBusy] = useState(false);
  const [genThumbBusy, setGenThumbBusy] = useState(false);
  const [thumbCostSeconds, setThumbCostSeconds] = useState(thumbnailCostSeconds(1));
  // Tras 3 fallos seguidos asumimos que es un bloqueo de contenido de la IA (personaje real
  // nombrado + tema de salud, típicamente) y no un fallo puntual de red/cuota — dejamos de
  // ofrecer "Crear con IA" para no seguir gastando llamadas a Gemini contra algo que va a
  // volver a fallar igual. Mismo umbral y misma idea que TabThumbnail.jsx (allí en 2; aquí en
  // 3 porque ya vimos en producción exactamente 3 intentos fallidos seguidos para un caso real
  // de este tipo de bloqueo).
  const [thumbFailCount, setThumbFailCount] = useState(0);
  const aiThumbBlocked = thumbFailCount >= 3;
  const pollRef = useRef();
  const thumbFileRef = useRef();
  const thumbUrl = project?.custom_thumbnail?.url || out?.thumbnail || "";
  const hasThumb = !!project?.custom_thumbnail?.url;

  // Coste real desde el backend (mismo espejo del markup que usa TabThumbnail.jsx) — el
  // estimado local de thumbnailCostSeconds() solo es el valor de respaldo si la llamada falla.
  useEffect(() => {
    let cancel = false;
    api.post("/cost/estimate", { action: "thumbnail", count: 1 })
      .then(({ data }) => { if (!cancel && data?.seconds != null) setThumbCostSeconds(data.seconds); })
      .catch(() => {});
    return () => { cancel = true; };
  }, []);

  // Miniatura ausente (el auto-generado del backend falló o el plan no la incluye): el
  // usuario no debería tener que salir a la pestaña clásica para resolverlo — mismos
  // endpoints que TabThumbnail.jsx, versión compacta de un solo paso (sin variantes que
  // elegir: genera UNA y la deja seleccionada directamente).
  const uploadThumb = async (file) => {
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) { toast.error(t("dl.thumbTooBig")); return; }
    setThumbBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/projects/${projectId}/thumbnail`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("dl.thumbUploaded"));
    } catch (e) { toast.error(apiErr(e)); } finally { setThumbBusy(false); }
  };
  const generateThumb = async () => {
    if (aiThumbBlocked) return;
    setGenThumbBusy(true);
    try {
      const data = await startThumbnail(projectId, project?.name, 1);
      const v = data?.new?.[0];
      if (v) { await api.post(`/projects/${projectId}/thumbnail/select`, { url: v.url, path: v.path }); toast.success(t("dl.aiThumbDone")); }
      else setThumbFailCount((c) => c + 1);
    } catch (e) { toast.error(apiErr(e)); setThumbFailCount((c) => c + 1); } finally { setGenThumbBusy(false); }
  };

  const loadStatus = async () => {
    try { const { data } = await api.get("/youtube/status", { params: { channel_id: project?.channel_id } }); setYt(data); }
    catch (e) { setYt({ connected: false }); }
  };
  const loadUpload = async () => {
    try {
      const { data } = await api.get(`/projects/${projectId}/youtube/upload-status`);
      setUpload(data);
      if (data.status === "uploading") { clearTimeout(pollRef.current); pollRef.current = setTimeout(loadUpload, 4000); }
    } catch (e) { /* silencioso */ }
  };
  useEffect(() => { loadStatus(); loadUpload(); return () => clearTimeout(pollRef.current); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // Si el vídeo viene de una entrada de calendario, la fecha de publicación programada allí
  // se usa como valor por defecto del programador — el usuario no tiene que volver a pensarla.
  useEffect(() => {
    api.get(`/projects/${projectId}/calendar-entry`).then((r) => {
      const d = r.data?.date;
      if (d && d >= new Date().toISOString().slice(0, 10)) { setSchedule(true); setPublishAt(`${d}T12:00`); }
    }).catch(() => {});
  }, [projectId]);

  // Estadísticas reales del vídeo recién subido (mismo endpoint que Mis Vídeos/pestaña
  // Descarga). Justo tras subir es normal ver ceros o que falte subscribers_gained un rato —
  // vistas/likes tardan en propagarse y la Analytics API va más lenta que la Data API.
  useEffect(() => {
    if (upload?.status !== "done" || !upload.video_id || !project?.channel_id) { setYtStats(null); return; }
    api.get(`/channels/${project.channel_id}/youtube/video-stats`, { params: { video_ids: upload.video_id } })
      .then(({ data }) => setYtStats(data[upload.video_id] || null))
      .catch(() => setYtStats(null));
  }, [upload?.status, upload?.video_id, project?.channel_id]);

  const copy = async (text, key) => {
    try { await navigator.clipboard.writeText(text); }
    catch (e) {
      const ta = document.createElement("textarea");
      ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0";
      document.body.appendChild(ta); ta.select();
      try { document.execCommand("copy"); } catch (_) {}
      document.body.removeChild(ta);
    }
    setCopied(key); setTimeout(() => setCopied(""), 1500);
    toast.success(t("dl.copied"));
  };
  const connect = async () => {
    setConnecting(true);
    try {
      await connectYoutubePopup(project?.channel_id);
      toast.success(t("dl.connected"));
      await loadStatus();
    } catch (e) {
      if (e.message !== "popup_closed") toast.error(apiErr(e));
    } finally { setConnecting(false); }
  };
  const disconnect = async () => {
    if (!window.confirm(t("dl.disconnectConfirm"))) return;
    try { await api.post("/youtube/disconnect", null, { params: { channel_id: project?.channel_id } }); setYt({ connected: false }); toast.success(t("dl.disconnected")); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const doUpload = async () => {
    const body = {
      title: title.trim(), description, privacy: schedule ? "private" : privacy,
      contains_synthetic_media: aiDisclosure,
    };
    if (schedule) {
      if (!publishAt) { toast.warning(t("dl.chooseDateTime")); return; }
      body.publish_at = new Date(publishAt).toISOString();
    }
    try {
      await api.post(`/projects/${projectId}/youtube/upload`, body);
      setUpload({ status: "uploading" });
      // Píldora en la cabecera: subir tarda minutos y hasta ahora solo se veía en ESTA pantalla,
      // así que el usuario que se movía a otra parte perdía de vista su propia subida.
      startYoutubeUpload(projectId, project?.name || title.trim());
      toast.success(t("dl.uploadingToast"));
      clearTimeout(pollRef.current); pollRef.current = setTimeout(loadUpload, 3000);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const inputCls = "w-full border-2 border-slate-300 rounded-lg px-3 py-2.5 text-base outline-none focus:border-amber-500";
  const privacyLabels = { private: t("dl.private"), unlisted: t("dl.unlisted"), public: t("dl.public") };

  return (
    <div className="space-y-6" data-testid="oc-publish-stage">
      <Card className="p-6 border-2 border-emerald-300 bg-gradient-to-br from-emerald-50 to-teal-50">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <CheckCircle2 size={28} className="shrink-0 mt-0.5 text-emerald-600" />
            <div>
              <h2 className="text-2xl font-bold text-slate-900">{t("oc.videoReadyTitle")}</h2>
              <p className="text-slate-700 mt-1">{t("oc.videoReadyBody")}</p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row w-full sm:w-auto items-stretch sm:items-center gap-2 sm:shrink-0">
            {onCreateAnother && (
              <button data-testid="oc-create-another-button" onClick={onCreateAnother}
                className="min-h-[48px] px-5 text-base font-bold rounded-xl bg-gradient-to-br from-brand-400 to-brand-700 hover:brightness-90 text-white shadow-md hover:shadow-lg flex items-center justify-center gap-2 w-full sm:w-auto">
                <Wand2 size={20} /> {t("oc.createAnother")}
              </button>
            )}
            {onRecreate && (
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button data-testid="oc-recreate-button" onClick={onRecreate}
                      className="min-h-[48px] px-5 text-base font-bold rounded-xl border-2 border-emerald-300 bg-white text-emerald-700 hover:bg-emerald-50 flex items-center justify-center gap-2 w-full sm:w-auto">
                      <RefreshCw size={20} /> {t("oc.recreateVideo")}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-[280px] bg-slate-900 text-white text-sm font-medium leading-snug px-3 py-2 rounded-xl shadow-2xl border border-white/10">
                    {t("oc.recreateVideoTip")}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="p-6">
          <video data-testid="oc-video-player" src={out?.video_url} poster={thumbUrl} controls className="w-full rounded-lg border-2 border-slate-200 bg-black aspect-video" />
          <a data-testid="oc-download-video-button" href={out?.video_url} download="final.mp4" target="_blank" rel="noreferrer" className={`mt-4 w-full ${btnPrimary}`}>
            <Download size={20} /> {t("dl.downloadMp4")}
          </a>
        </Card>

        <Card className="p-5" data-testid="oc-thumbnail-status">
          <div className="flex flex-col items-center text-center gap-3">
            <div className="w-full aspect-video rounded-lg border-2 border-slate-200 bg-slate-100 overflow-hidden flex items-center justify-center">
              {hasThumb ? <img src={thumbUrl} alt={t("dl.thumbAlt")} className="w-full h-full object-cover" /> : <ImageIcon size={26} className="text-slate-300" />}
            </div>
            <div className="w-full">
              <h4 className="text-base font-bold text-slate-900">{t("dl.thumbSectionTitle")}</h4>
              <p className="text-sm text-slate-500 mt-0.5">{hasThumb ? t("dl.thumbReady") : t("dl.thumbMissing")}</p>
              {/* Botones SIEMPRE visibles, no solo cuando falta miniatura — la auto-generada
                  al terminar el 1-clic (_auto_generate_thumbnail_for_one_click) no tiene por
                  qué ser la que el usuario quiere usar; debe poder regenerar otra con IA o
                  subir la suya en cualquier momento, no solo cuando no hay ninguna. */}
              <div className="flex flex-wrap justify-center gap-2 mt-2.5">
                <input ref={thumbFileRef} type="file" accept="image/jpeg,image/png" className="hidden" data-testid="oc-thumb-file-input" onChange={(e) => uploadThumb(e.target.files?.[0])} />
                {!aiThumbBlocked && (
                  <button data-testid="oc-thumb-generate" onClick={generateThumb} disabled={genThumbBusy || thumbBusy}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-sm font-semibold disabled:opacity-60">
                    {genThumbBusy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} {hasThumb ? t("dl.regenerateThumb") : t("dl.createThumb")}
                    {!genThumbBusy && <CostBadge seconds={thumbCostSeconds} tip={t("dl.aiThumbCostTip")} />}
                  </button>
                )}
                <button data-testid="oc-thumb-upload" onClick={() => thumbFileRef.current?.click()} disabled={genThumbBusy || thumbBusy}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border-2 border-slate-300 text-slate-700 text-sm font-semibold hover:border-slate-400 disabled:opacity-60">
                  {thumbBusy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} {t("dl.uploadThumb")}
                </button>
              </div>
              {aiThumbBlocked && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-2 mt-2.5" data-testid="oc-thumb-ai-blocked">
                  {t("dl.aiThumbBlocked")}
                </p>
              )}
            </div>
          </div>
        </Card>

        <Card className="p-6" data-testid="oc-youtube-section">
            <div className="flex items-center justify-between gap-2 mb-4">
              <div className="flex items-center gap-2">
                <Youtube className="text-red-600" size={24} />
                <h3 className="text-lg font-bold text-slate-900">{t("dl.ytTitle")}</h3>
              </div>
              {yt?.connected && (
                <button onClick={disconnect} className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-red-600"><Unplug size={14} /> {t("dl.disconnect")}</button>
              )}
            </div>

            {yt === null ? (
              <div className="py-6 flex justify-center"><Loader2 className="animate-spin text-slate-400" size={26} /></div>
            ) : !yt.connected ? (
              <div className="space-y-5" data-testid="oc-yt-not-connected">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-bold text-slate-900">{t("dl.ytDesc")}</h4>
                    <button onClick={() => copy(description, "desc")} className="flex items-center gap-1 text-amber-700 text-sm font-semibold hover:text-amber-900">
                      {copied === "desc" ? <Check size={15} /> : <Copy size={15} />} {t("dl.copy")}
                    </button>
                  </div>
                  <textarea readOnly value={description} className="w-full h-24 border-2 border-slate-200 rounded-lg p-3 text-sm bg-slate-50 resize-none" />
                </div>
                <div className="border-t border-slate-200 pt-4">
                  <p className="text-sm text-slate-600 mb-3"><Trans i18nKey="dl.publishHere" components={{ 0: <b /> }} /></p>
                  <button onClick={connect} disabled={connecting} className="inline-flex items-center justify-center gap-2 px-4 sm:px-5 min-h-[48px] text-base rounded-xl bg-red-600 text-white font-bold hover:bg-red-700 disabled:opacity-60">
                    {connecting ? <Loader2 className="animate-spin" size={18} /> : <Youtube size={20} />} {t("dl.connectYt")}
                  </button>
                </div>
              </div>
            ) : upload?.status === "done" ? (
              <div data-testid="oc-yt-upload-done" className="space-y-2.5">
                <Alert type="success"><Trans i18nKey="dl.uploadedTitle" values={{ privacy: upload.privacy, scheduled: upload.publish_at ? t("dl.scheduledFor", { date: new Date(upload.publish_at).toLocaleString() }) : "" }} components={{ 0: <b /> }} /></Alert>
                {ytStats && (
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm font-semibold text-slate-600" data-testid="oc-yt-upload-stats">
                    <span className="flex items-center gap-1"><Eye size={15} /> {fmtCompact(ytStats.views)}</span>
                    <span className="flex items-center gap-1"><ThumbsUp size={15} /> {fmtCompact(ytStats.likes)}</span>
                    <span className="flex items-center gap-1"><MessageCircle size={15} /> {fmtCompact(ytStats.comments)}</span>
                    {ytStats.subscribers_gained != null && (
                      <span title={t("videos.subsGainedTip")} className="flex items-center gap-1 text-emerald-700">
                        <UserPlus size={15} /> {fmtCompact(ytStats.subscribers_gained)}
                      </span>
                    )}
                    {ytStats.watch_time_minutes != null && (
                      <span title={t("videos.watchTimeTip")} className="flex items-center gap-1">
                        <Timer size={15} /> {fmtWatchTime(ytStats.watch_time_minutes)}
                      </span>
                    )}
                    {ytStats.avg_view_percentage != null && (
                      <span title={t("videos.retentionTip")} className="flex items-center gap-1">
                        <PieChart size={15} /> {ytStats.avg_view_percentage}%
                      </span>
                    )}
                  </div>
                )}
                {/* Mismo panel que en la pestaña clásica Descarga: el 1-clic es el flujo por el
                    que pasa la mayoría, así que la retención tiene que verse también aquí. */}
                <div className="border-t border-slate-200 pt-3">
                  <RetentionPanel projectId={projectId} />
                </div>
                <div className="pt-1">
                  <a href={upload.url} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-2 min-h-[44px] px-5 text-base font-bold rounded-xl bg-red-600 text-white hover:bg-red-700 transition-colors"><Youtube size={18} /> {t("dl.viewYt")}</a>
                  <button onClick={() => setUpload({ status: "idle" })} className="ml-3 text-amber-700 font-semibold hover:text-amber-900">{t("dl.uploadAgain")}</button>
                </div>
              </div>
            ) : (
              <div className="space-y-4" data-testid="oc-yt-upload-form">
                <span className="text-sm text-slate-600"><Trans i18nKey="dl.connectedAs" values={{ channel: yt.channel_title || t("dl.yourChannel") }} components={{ 0: <b className="text-slate-900" /> }} /></span>
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-1.5">{t("dl.labelTitle")}</label>
                  <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={100} className={inputCls} />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="block text-sm font-bold text-slate-700">{t("dl.labelDesc")}</label>
                    <button onClick={() => copy(description, "desc")} className="flex items-center gap-1 text-amber-700 text-sm font-semibold hover:text-amber-900">{copied === "desc" ? <Check size={14} /> : <Copy size={14} />} {t("dl.copy")}</button>
                  </div>
                  <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} className={`${inputCls} resize-none`} />
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={aiDisclosure} onChange={(e) => setAiDisclosure(e.target.checked)} className="w-5 h-5 accent-amber-600" />
                  <span className="text-sm font-semibold text-slate-800 flex items-center gap-1.5"><Sparkles size={16} className="text-amber-600" /> {t("dl.aiDisclosure")}</span>
                </label>
                <p className="text-xs text-slate-500 -mt-2 ml-7">{t("dl.aiDisclosureHint")}</p>
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-1.5">{t("dl.privacy")}</label>
                  <div className="flex flex-wrap gap-2">
                    {["private", "unlisted", "public"].map((v) => {
                      const locked = schedule || (v === "public" && !hasThumb);
                      return (
                        <button key={v} disabled={locked} onClick={() => setPrivacy(v)}
                          title={v === "public" && !hasThumb ? t("dl.publicThumbHint") : ""}
                          className={`px-4 py-2 rounded-lg font-semibold border-2 ${(!schedule && privacy === v) ? "border-amber-500 bg-amber-50 text-amber-800" : "border-slate-200 text-slate-600"} ${locked ? "opacity-40 cursor-not-allowed" : ""}`}>{privacyLabels[v]}</button>
                      );
                    })}
                  </div>
                  {!hasThumb && <p className="text-xs text-amber-700 mt-1.5"><Trans i18nKey="dl.noThumbHint" components={{ 0: <b />, 1: <b /> }} /></p>}
                </div>

                <label className={`flex items-center gap-2 ${hasThumb ? "cursor-pointer" : "opacity-40 cursor-not-allowed"}`}>
                  <input type="checkbox" disabled={!hasThumb} checked={schedule} onChange={(e) => setSchedule(e.target.checked)} className="w-5 h-5 accent-amber-600" />
                  <span className="text-sm font-semibold text-slate-800 flex items-center gap-1.5"><CalendarClock size={16} className="text-amber-600" /> {t("dl.schedulePub")}</span>
                </label>
                {schedule && (
                  <input type="datetime-local" value={publishAt} onChange={(e) => setPublishAt(e.target.value)} className={`${inputCls} sm:w-72`} />
                )}
                {schedule && <p className="text-xs text-slate-500">{t("dl.scheduleHint")}</p>}

                {upload?.status === "error" && <Alert type="error">{t("dl.uploadError", { error: upload.error })}</Alert>}

                <button onClick={doUpload} disabled={upload?.status === "uploading"} className={`${btnPrimary} w-full sm:w-auto px-6 bg-red-600 hover:bg-red-700`}>
                  {upload?.status === "uploading" ? <Loader2 className="animate-spin" size={18} /> : <Upload size={18} />}
                  {upload?.status === "uploading" ? t("dl.uploading") : (schedule ? t("dl.scheduleYt") : t("dl.uploadYt"))}
                </button>
              </div>
            )}
          </Card>
      </div>

      <ShortsCard project={project} />
    </div>
  );
}

export default function OneClickProducer({ projectId, onBackToChannel, onEnterProject, onDismissToEditor, onProjectPolled, onCreateAnother }) {
  const { t } = useTranslation();
  const { getRender, startRender, startFill, isFilling } = useUploads();
  const [status, setStatus] = useState(null);
  const [project, setProject] = useState(null);
  const [rejecting, setRejecting] = useState(false);
  const [approving, setApproving] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [replaceTarget, setReplaceTarget] = useState(null); // clip que se está reemplazando (popup "Mi carpeta")
  const [library, setLibrary] = useState({ videos: [], folders: [] });
  const [loadingLib, setLoadingLib] = useState(false);
  // Preview de vídeo del panel de revisión: antes se reproducía automáticamente al pasar el
  // ratón por encima (bug real reportado: intermitente, interferido por el overlay de
  // "reemplazar" que estaba encima en el stacking). Sustituido por un botón explícito — solo
  // un clip reproduce a la vez, así que basta con guardar SU id.
  const [previewPlayingId, setPreviewPlayingId] = useState(null);
  const togglePreview = (id) => setPreviewPlayingId((cur) => (cur === id ? null : id));
  const timerRef = useRef(null);

  // Etiquetas de fuente localizadas (fallback al mapa fijo si falta la clave)
  const sourceLabel = (src) => t(`oc.source.${src}`, { defaultValue: SOURCE_LABELS[src] || src });

  // Mensajes que rotan durante la fase de guion (el LLM devuelve todo de golpe, así que no hay
  // datos parciales que enseñar — pero la espera no tiene por qué ser un spinner mudo).
  const scriptMsgs = t("oc.scriptMsgs", { returnObjects: true });
  // Mismo criterio para la fase de composición (montador IA: transiciones, movimiento de
  // cámara, rótulos) — antes mostraba una única línea estática (status.step_detail), ahora
  // rota igual que scriptMsgs, reutilizando el mismo msgIdx/temporizador.
  const composingMsgs = t("oc.composingMsgs", { returnObjects: true });
  const STAGES = STAGE_KEYS.map((key) => ({ key, icon: STAGE_ICONS[key], label: t(`oc.step.${key}`) }));

  const load = useCallback(async () => {
    try {
      const [{ data: st }, { data: p }] = await Promise.all([
        api.get(`/projects/${projectId}/one-click/status`),
        api.get(`/projects/${projectId}`),
      ]);
      setStatus(st);
      setProject(p);
      // Mantiene fresco el `project` del componente padre (Builder) con el MISMO poll —
      // sin esto, Builder no se entera de que el render terminó (project.render.status)
      // y se quedaba mostrando esta pantalla indefinidamente o, antes, saltaba a los tabs
      // clásicos de golpe en cuanto arrancaba el render por no tener el dato actualizado.
      onProjectPolled && onProjectPolled(p);
    } catch (e) { /* silencioso, seguir polling */ }
  }, [projectId, onProjectPolled]);

  useEffect(() => {
    load();
    timerRef.current = setInterval(load, 4000);
    return () => clearInterval(timerRef.current);
  }, [load]);

  // Popup "Mi carpeta" para reemplazar un clip concreto del panel de revisión de material.
  const loadLibrary = useCallback(async () => {
    setLoadingLib(true);
    try {
      const { data } = await api.get(`/projects/${projectId}/broll/library`);
      setLibrary(data);
    } catch (e) { toast.error(apiErr(e)); } finally { setLoadingLib(false); }
  }, [projectId]);

  // "clips" se declaraba más abajo (junto a clipsByScene) pero personajesFlojos lo necesita
  // aquí — estaba referenciado antes de su punto de declaración (temporal dead zone):
  // "Cannot access 'clips' before initialization" en TODO proyecto que llegara a esta
  // pantalla, no un caso puntual (bug real reportado ago-2026).
  const clips = (project?.broll?.clips) || [];

  const openReplace = (c) => { setReplaceTarget(c); loadLibrary(); };
  const closeReplace = () => setReplaceTarget(null);

  const replaceClip = async (newClips) => {
    const pick = (newClips || [])[0];
    if (!pick || !replaceTarget) return;
    // La duración del reemplazo tiene que ser EXACTAMENTE la del clip sustituido — si no, el
    // resto de clips de la escena se desplaza en el tiempo (la narración ya está grabada con
    // esos huecos fijos). Para vídeo, además fijamos start/end para que se recorte a esa
    // duración exacta en vez de usar el clip fuente entero.
    const duration = replaceTarget.duration;
    const isImage = pick.media === "image";
    // director_source/director_query del clip NUEVO (no del sustituido): así el badge y el
    // tooltip de procedencia del panel de revisión ("Pexels", "Unsplash"...) y el texto de
    // búsqueda quedan correctos tras reemplazar, en vez de mostrar "?" o quedar vacíos.
    const next = clips.map((c) => (c.id === replaceTarget.id ? {
      ...pick, id: replaceTarget.id, segment_id: replaceTarget.segment_id,
      graphic_origin: replaceTarget.graphic_origin || replaceTarget.graphic,
      director_source: pick.director_source || undefined,
      director_query: pick.director_query || undefined,
      needs_review: pick.needs_review,
      duration, ...(isImage ? {} : { start: 0, end: duration }),
    } : c));
    try {
      await api.put(`/projects/${projectId}/broll`, { clips: next });
      toast.success(t("oc.clipReplaced"));
      closeReplace();
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  // El render, una vez arrancado, ya lo sigue el tracker GLOBAL (UploadContext.startRender) —
  // la sesión de producción en 1 clic lo entrega automáticamente en cuanto detecta
  // one_click.status:"rendering" (ver _pollOneClick). Leemos de ahí en vez de solo el
  // project plano porque el tracker global usa el endpoint enriquecido /render/status: incluye
  // progreso fino en vivo, el detalle de qué escena se está componiendo AHORA, y detección de
  // renders huérfanos (si el servidor se reinició a mitad, lo marca "error" automáticamente en
  // vez de quedarse "rendering" para siempre).
  const globalRender = getRender ? getRender(projectId) : null;
  const liveRender = globalRender?.raw || project?.render || {};

  // Si el render ya está en curso pero el tracker global no lo tiene (p. ej. se entró a este
  // proyecto directamente — recarga de página, o desde la lista de vídeos — en vez de haber
  // arrancado el 1-clic en ESTA pestaña, que es el único caso que dispara el hand-off
  // automático en UploadContext._pollOneClick), lo registramos aquí. Sin esto, la escena en
  // vivo (detail_items — "qué escena se está componiendo ahora") solo aparecía en la pestaña
  // clásica de Renderizar, nunca en esta pantalla, aunque el render real fuera el mismo.
  // Mismo patrón que TabRenderizar.
  useEffect(() => {
    if (project?.render?.status === "rendering" && !globalRender) {
      startRender(projectId, status?.titulo || project?.name, { resume: true });
    }
  }, [project?.render?.status, globalRender, projectId, status?.titulo, project?.name, startRender]);

  // Cuando el render de verdad TERMINA CON ÉXITO ya NO se navega a los tabs clásicos: se
  // muestra la pantalla final (PublishStage) aquí mismo — miniatura + vídeo + publicar en
  // YouTube, todo sin salir del componente de 1-clic (la promesa explícita del flujo).
  // OJO: one_click.status pasa a "rendering" y se queda ahí para siempre (nada lo cierra a
  // "completo") — antes este efecto saltaba a los tabs clásicos en cuanto ARRANCABA el
  // render (la fase más larga), cortando en seco la escaleta viva justo cuando más tardaba.
  // Si el render FALLA, tampoco se navega — se queda aquí con un botón "Reintentar" (ver más
  // abajo), para no soltar al usuario en el asistente clásico justo cuando algo salió mal.
  const liveRenderStatus = liveRender.status;
  const renderFailed = liveRenderStatus === "error";
  // Guarda contra un render.status "completo" HEREDADO de un ciclo anterior: si la sesión de
  // 1-clic sigue "en_curso" en un paso ANTERIOR a "render" (p. ej. recién retomada desde
  // "dismissed" con step:"media", reuniendo material de nuevo), ese vídeo completo no es de
  // ESTA sesión — no hay que saltar a "¡Tu vídeo está listo!" mientras todavía se está
  // trabajando en un paso anterior (bug real: pasaba con proyectos que ya habían renderizado
  // una vez y luego se relanzó el 1-clic sobre el mismo guion).
  const oneClickStillWorkingEarlier = status?.status === "en_curso" && status?.step !== "render";
  const renderComplete = liveRenderStatus === "completo" && !oneClickStillWorkingEarlier;

  // Reintentar el render tras un fallo: reutiliza el mismo endpoint que el botón "Crear vídeo"
  // clásico (POST /render). run_render() lee guion/narración/B-roll YA GUARDADOS en Mongo (no
  // los rehace) y reutiliza del caché de Backblaze cualquier escena que ya se hubiera compuesto
  // con éxito en el intento anterior (el caché solo se borra cuando el render tiene éxito) — un
  // reintento nunca vuelve a cobrar: require_seconds solo COMPRUEBA saldo, deduct_seconds (el
  // único cobro real) solo ocurre si el render termina bien.
  const retryRender = async () => {
    setRetrying(true);
    try {
      await startRender(projectId, status?.titulo || project?.name);
    } finally {
      setRetrying(false);
    }
  };

  // Reintentar el pipeline de 1-clic tras un fallo PRE-render (narración/material/composición):
  // el guion ya existe (se generó con éxito antes de fallar), así que reutiliza el mismo
  // endpoint que "1 clic desde un guion ya escrito" — skip_script=True en el backend, nunca
  // vuelve a generar ni cobrar el guion. Solo tiene sentido si ya hay guion (≥2 escenas); si
  // falló en la propia fase de guion no hay nada que retomar, hay que crear el vídeo de nuevo.
  const [retryingOc, setRetryingOc] = useState(false);
  const retryOneClick = async () => {
    setRetryingOc(true);
    try {
      await api.post(`/projects/${projectId}/one-click/from-script`, {
        voice: status?.voice || "", music: status?.music !== false, auto_render: !!status?.auto_render,
      });
      toast.success(t("oc.started"));
      await load();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setRetryingOc(false); }
  };

  // ── Reloj de producción + rotación de mensajes de la fase guion ──
  const [nowTs, setNowTs] = useState(Date.now());
  const [msgIdx, setMsgIdx] = useState(0);
  const [playingSeg, setPlayingSeg] = useState(null);
  const segAudioRef = useRef(null);

  // El backend puede pasar one_click.status a "rendering" mientras el paso todavía reportado
  // (status.step) sigue siendo "composing" — el montador decide transiciones/movimiento de
  // cámara ANTES de arrancar el render real (ver el comentario sobre currentIndex/stageIdx más
  // abajo). Si la rotación solo dependiera de status==="en_curso" se paraba justo en ese
  // instante y la pantalla se quedaba pegada en la primera frase de composingMsgs (bug real
  // reportado: "las frases del montador no pasan, solo se ve la primera").
  const inProgress = status?.status === "en_curso" || status?.step === "composing";
  useEffect(() => {
    if (!inProgress) return;
    const tick = setInterval(() => setNowTs(Date.now()), 1000);
    const rot = setInterval(() => setMsgIdx((i) => i + 1), 6000);
    return () => { clearInterval(tick); clearInterval(rot); };
  }, [inProgress]);

  // Parar el audio de preescucha al salir de la pantalla.
  useEffect(() => () => { if (segAudioRef.current) segAudioRef.current.pause(); }, []);

  // Pide permiso de notificaciones del navegador una sola vez, sin bloquear nada si se deniega
  // o el navegador no lo soporta — el email de aviso (ya existe en el backend para
  // awaiting_approval, render completo y render fallido) sigue funcionando pase lo que pase.
  useEffect(() => {
    if (typeof Notification !== "undefined" && Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }
  }, []);

  // Aviso sonoro + notificación del navegador al llegar a un punto en el que el usuario, si se
  // ha ido a hacer otra cosa, necesita volver: material listo para revisar, vídeo terminado, o
  // cualquiera de los dos errores. Se dispara UNA vez por estado (no en cada poll de 4s) gracias
  // al ref que recuerda el último estado ya avisado.
  const notifiedKeyRef = useRef(null);
  useEffect(() => {
    let key = null;
    if (status?.status === "awaiting_approval") key = "awaiting_approval";
    else if (liveRenderStatus === "completo") key = "completo";
    else if (liveRenderStatus === "error") key = "render_error";
    else if (status?.status === "error") key = "oc_error";
    if (key && notifiedKeyRef.current !== key) {
      notifiedKeyRef.current = key;
      playChime();
      if (typeof Notification !== "undefined" && Notification.permission === "granted" && document.hidden) {
        try { new Notification(t("oc.notifTitle"), { body: t(`oc.notifBody.${key}`) }); } catch (e) { /* silencioso */ }
      }
    }
  }, [status?.status, liveRenderStatus, t]);

  // Preescucha de la narración por escena: en cuanto un segmento tiene audio_url real,
  // el usuario puede OÍR a su narrador mientras el resto de escenas se sigue grabando.
  const playSeg = (seg) => {
    if (playingSeg === seg.id && segAudioRef.current) {
      segAudioRef.current.pause();
      setPlayingSeg(null);
      return;
    }
    if (segAudioRef.current) segAudioRef.current.pause();
    const a = new Audio(seg.audio_url);
    segAudioRef.current = a;
    a.onended = () => setPlayingSeg(null);
    a.play().then(() => setPlayingSeg(seg.id)).catch(() => {});
  };

  const approve = async () => {
    setApproving(true);
    try {
      await api.post(`/projects/${projectId}/one-click/approve`);
      toast.success(t("oc.sentToRender"));
      if (onEnterProject) onEnterProject(projectId);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setApproving(false); }
  };

  const reject = async () => {
    if (!window.confirm(t("oc.confirmReject"))) return;
    setRejecting(true);
    try {
      await api.post(`/projects/${projectId}/one-click/reject`);
      toast.success(t("oc.regenerating"));
    } catch (e) { toast.error(apiErr(e)); }
    finally { setRejecting(false); }
  };

  // Desde la pantalla de "vídeo listo": muestra el panel de revisión de material (mismos clips,
  // el usuario cambia lo que quiera vía clic-para-reemplazar) SIN tocar el estado real del
  // proyecto en la BD (sigue "Completado" hasta que de verdad se relance el render) — es una
  // vista puramente local, a diferencia de "reject" (que sí muta el proyecto y rebusca el B-roll).
  const [showMaterialEdit, setShowMaterialEdit] = useState(false);
  const [rerendering, setRerendering] = useState(false);
  const rerenderVideo = async () => {
    setRerendering(true);
    try {
      const ok = await startRender(projectId, status?.titulo || project?.name);
      if (ok) setShowMaterialEdit(false);
    } finally { setRerendering(false); }
  };

  // Regenera TODO el B-roll desde cero (borra el material actual y lo vuelve a buscar con el
  // motor vigente) — para cuando el material se generó con una versión antigua del relleno de
  // tarjetas o mientras el crédito de imágenes IA estaba agotado. A diferencia de "Rechazar"
  // (solo disponible en awaiting_approval), esto funciona con el proyecto ya renderizado.
  const [regeneratingAll, setRegeneratingAll] = useState(false);
  const regenerateAllBroll = async () => {
    if (!window.confirm(t("oc.confirmRegenerateAll"))) return;
    setRegeneratingAll(true);
    try {
      await api.post(`/projects/${projectId}/broll/regenerate-all`);
      toast.success(t("oc.regeneratingAll"));
      await load();
    } catch (e) { toast.error(apiErr(e)); } finally { setRegeneratingAll(false); }
  };

  // Completa SOLO las escenas que se quedaron sin material suficiente, conservando (y sin volver
  // a cobrar) las que ya están bien. Para cuando una búsqueda se corta a medias por un fallo
  // ajeno al usuario — p.ej. una fuente externa que nos limita las peticiones — y antes la
  // única salida era regenerar el vídeo entero desde cero.
  // Va por el contexto global (no por una petición local) para que el trabajo se vea en la barra
  // de tareas de la cabecera y sobreviva a salir de esta pantalla — puede tardar 40 min.
  const fillingGaps = isFilling(projectId);
  const fillBrollGaps = async () => {
    await startFill(projectId, project?.name || status?.titulo);
  };

  // Si al abrir ya hay un completado en marcha (recarga de página, o se lanzó desde otra
  // pestaña), se reengancha el sondeo en vez de dejar la barra vacía y el botón habilitado.
  useEffect(() => {
    if (!projectId || isFilling(projectId)) return;
    let cancel = false;
    api.get(`/projects/${projectId}/broll/fill-gaps/status`)
      .then(({ data }) => { if (!cancel && data?.active) startFill(projectId, project?.name, { resume: true }); })
      .catch(() => {});
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const [switchingManual, setSwitchingManual] = useState(false);

  if (!status) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 size={32} className="animate-spin text-amber-500" />
      </div>
    );
  }

  const currentStep = status.step;
  // one_click.status puede quedarse CONGELADO en "awaiting_approval" para siempre: el botón de
  // render manual (POST /projects/{pid}/render, vista clásica) nunca toca one_click.* — son
  // namespaces de estado independientes en el backend (ver start_render). Si un proyecto pasó
  // por el asistente de 1-clic, se quedó esperando aprobación, y LUEGO se renderizó desde vista
  // manual, one_click.status sigue diciendo "awaiting_approval" mientras el render real ya
  // corre. Bug real reportado: al cambiar de vista manual a 1-clic con un render en curso, esta
  // pantalla volvía a mostrar la revisión de material como si aún hubiera que aprobar algo.
  // liveRenderStatus (render.status real, ver arriba) manda siempre que diga "rendering".
  const isRendering = status.status === "rendering" || liveRenderStatus === "rendering";
  // `!renderComplete` por la MISMA razón que `!isRendering`, y es el caso que faltaba: si el
  // vídeo ya está renderizado, no hay nada que aprobar aunque one_click siga congelado en
  // "awaiting_approval". Sin esto se pintaban las DOS zonas a la vez —el vídeo listo para
  // publicar arriba y la revisión de material debajo—, que es un estado imposible y desconcierta
  // (visto en producción, ago-2026). La revisión posterior a un render sigue existiendo, pero
  // solo cuando el usuario la pide: es la rama `renderComplete && showMaterialEdit` de más abajo.
  const isDone = status.status === "awaiting_approval" && !isRendering && !renderComplete;
  const isError = status.status === "error";

  // A qué pestaña de la vista manual aterrizar al cambiar: la que corresponde a lo que está
  // pasando AHORA, no siempre B-roll — si está renderizando (o el render ya terminó/falló),
  // lo relevante es la pantalla de Renderizar, no el editor de B-roll.
  const STEP_TO_TAB = { script: "guion", narration: "narracion", media: "broll", composing: "composicion", render: "renderizar" };
  const manualTargetTab = (renderComplete || renderFailed || isRendering) ? "renderizar"
    : isDone ? "broll"
    : STEP_TO_TAB[currentStep] || "broll";

  // Botón de cabecera "Pasar a vista manual": el usuario puede salir del 1-clic en cualquier
  // momento y seguir en los tabs clásicos (Guion/Narración/B-roll/Renderizar…) — el render en
  // curso, si lo hay, NO se detiene, solo cambia la pantalla, aterrizando en la pestaña que
  // corresponde a lo que está pasando ahora (manualTargetTab). El backend bloquea el dismiss
  // SOLO si hay un render de verdad en curso (dinero comprometido en ese instante); en ese
  // caso el catch avisa con un toast en vez de fallar en silencio.
  const switchToManual = async () => {
    setSwitchingManual(true);
    try {
      if (onDismissToEditor) await onDismissToEditor(projectId, manualTargetTab);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSwitchingManual(false); }
  };

  const currentIndex = STAGE_KEYS.findIndex((k) => k === currentStep);
  // Estación activa del riel: la que reporte el backend (currentStep) SIEMPRE que sea válida —
  // incluye "composing" aunque one_click.status ya sea "rendering" (jul 2026: el backend se
  // queda en step="composing" mientras el montador decide transiciones/movimiento de cámara
  // ANTES de arrancar el render de verdad; forzar aquí a la última estación hacía que el rail
  // saltara a "Render" mientras esa fase seguía vacía — ver run_render en el backend). Solo cae
  // a "forzar última estación" cuando currentStep no es un paso reconocido (proyectos antiguos,
  // o una carrera con el doc de render mientras isRendering ya es cierto).
  // "en_cola" manda sobre currentStep: al aprobar, el backend deja one_click.step="composing" y
  // solo lo pasa a "render" cuando run_render arranca de verdad — pero si el render se queda en
  // cola (solo corre uno a la vez en todo el servidor, ver _enqueue_render), run_render no
  // arranca, así que el paso se quedaba clavado en "composing" con los mensajes del montador
  // rotando indefinidamente sobre un trabajo que ni había empezado. Se fuerza la última estación
  // para poder avisar de la espera, igual que hace la vista manual (ver RenderStage).
  const isQueued = liveRenderStatus === "en_cola";
  const stageIdx = isQueued ? STAGE_KEYS.length - 1
    : currentIndex >= 0 ? currentIndex : (isRendering ? STAGE_KEYS.length - 1 : 0);
  // El riel + su viewport único solo se muestran mientras la IA trabaja SOLA. Los puntos de
  // DECISIÓN (aprobar preview), de PROBLEMA (error) o de CIERRE (vídeo listo/publicar) tienen
  // su propia pantalla dedicada.
  const showAssemblyLine = !isError && !isDone && !renderFailed && !renderComplete;

  const segments = project?.segments || [];
  const clipsByScene = segments.map((s) => ({
    seg: s,
    clips: clips.filter((c) => c.segment_id === s.id),
  }));

  const startedRaw = project?.one_click?.started_at || "";
  const startedMs = startedRaw
    ? Date.parse(startedRaw.endsWith("Z") || startedRaw.includes("+") ? startedRaw : startedRaw + "Z")
    : null;
  const elapsedSec = startedMs ? Math.max(0, Math.floor((nowTs - startedMs) / 1000)) : 0;
  // Escena "activa" para resaltar en voz/material: la primera con voz pendiente, o la primera
  // sin material suficiente.
  const firstNarrPendingIdx = segments.findIndex((s) => s.narration_status !== "completo");
  const firstUnfilledIdx = clipsByScene.findIndex(({ seg, clips: c2 }) => {
    const au = seg.narration_duration || seg.duration || 0;
    return au > 0 && c2.reduce((x, c) => x + (c.duration || 0), 0) < au - 3.5; // = GRAPHIC_FILL_MIN_GAP
  });
  const musicMoodLabel = project?.one_click?.music_mood
    ? t(`comp.mood.${project.one_click.music_mood}`, { defaultValue: project.one_click.music_mood })
    : null;

  return (
    <div className="space-y-6">
      {/* HEADER */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
        <div className="flex items-start gap-4 min-w-0 flex-1">
          <button onClick={onBackToChannel} className="mt-1 text-slate-400 hover:text-slate-700 shrink-0" aria-label={t("oc.back")}>
            <ArrowLeft size={26} />
          </button>
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-brand-400 to-brand-700 flex items-center justify-center shadow-sm shrink-0">
                <Wand2 size={18} className="text-white" />
              </div>
              <span className="text-sm font-bold uppercase tracking-wide text-brand-700">{t("oc.badge")}</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 truncate" title={status.titulo || project?.name || t("oc.defaultVideoName")}>{status.titulo || project?.name || t("oc.defaultVideoName")}</h1>
          </div>
        </div>
        {/* Salida a los tabs clásicos desde cualquier punto del 1-clic, incluso a mitad de
            render: descartar NO lo detiene (es una tarea en el servidor, independiente de la
            pantalla) — la pestaña Renderizar clásica sabe mostrar el mismo progreso en vivo. */}
        {(showAssemblyLine || renderComplete) && (
          <button
            data-testid="oc-switch-manual"
            onClick={switchToManual}
            disabled={switchingManual}
            className="shrink-0 w-full sm:w-auto inline-flex items-center justify-center gap-2 min-h-[48px] px-4 sm:px-5 text-base rounded-xl border-2 border-slate-300 text-slate-600 font-bold hover:border-slate-400 hover:text-slate-800 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {switchingManual ? <Loader2 size={18} className="animate-spin" /> : <PenLine size={18} />}
            {t("oc.switchToManual")}
          </button>
        )}
      </div>

      {/* AVISO: puede cerrar la pestaña — le avisamos por email (ya existe en el backend para
          material listo / render completo / render fallido) y con un sonido si se queda aquí. */}
      {showAssemblyLine && (
        <div className="flex items-center gap-2.5 rounded-xl border-2 border-amber-200 bg-amber-50 px-4 py-2.5 text-sm font-medium text-amber-800" data-testid="oc-leave-notice">
          <Mail size={16} className="shrink-0" /> {t("oc.leaveNotice")}
        </div>
      )}

      {/* ERROR (pre-render: guion / narración / material) — nunca se muestra si el render ya
          terminó con éxito después (one_click.status puede quedarse pegado en "error" de un
          incidente previo, p. ej. un reinicio del servidor a mitad de render, aunque un
          reintento posterior sí completara el vídeo). Tampoco se muestra si YA hay un error de
          render más específico (renderFailed, justo abajo) — un reinicio del servidor a mitad
          de render deja los dos flags en "error" a la vez, y sin este guardián aparecían las
          DOS alarmas apiladas con el mismo mensaje y los mismos botones (bug real reportado). */}
      {isError && !renderComplete && !renderFailed && (
        <Alert type="error">
          <div className="flex items-start gap-3">
            <AlertTriangle size={22} className="shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-bold text-lg">{t("oc.errorTitle")}</div>
              <div className="text-base">{status.error || t("oc.errorUnknown")}</div>
              <div className="mt-3 flex flex-wrap gap-2">
                {(status.segments >= 2) && (
                  <button
                    data-testid="oc-error-retry"
                    onClick={retryOneClick}
                    disabled={retryingOc}
                    className={`${btnPrimary} bg-gradient-to-br from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 disabled:opacity-60`}
                  >
                    {retryingOc ? <Loader2 size={20} className="animate-spin" /> : <RefreshCw size={20} />}
                    {t("oc.retry")}
                  </button>
                )}
                <button data-testid="oc-error-back" onClick={onBackToChannel} className="min-h-[48px] px-4 sm:px-5 text-base rounded-xl border-2 border-slate-300 text-slate-700 font-bold hover:bg-slate-50">{t("oc.backToChannel")}</button>
                <button data-testid="oc-error-edit" onClick={() => onDismissToEditor && onDismissToEditor(projectId)} className="min-h-[48px] px-4 sm:px-5 text-base rounded-xl border-2 border-amber-500 bg-amber-50 text-amber-800 font-bold hover:bg-amber-100">{t("oc.editManually")}</button>
              </div>
            </div>
          </div>
        </Alert>
      )}

      {/* RENDER FALLIDO: el guion, la narración y el B-roll siguen intactos (nunca se rehacen
          aquí) y las escenas ya compuestas con éxito quedan cacheadas — "Reintentar" solo
          vuelve a componer lo que falló, sin perder el trabajo ya hecho ni cobrar dos veces
          (deduct_seconds solo ocurre si el render termina bien). */}
      {renderFailed && (
        <Alert type="error" testid="oc-render-error">
          <div className="flex items-start gap-3">
            <AlertTriangle size={22} className="shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-bold text-lg">{t("oc.renderFailedTitle")}</div>
              <div className="text-base">{liveRender.error || t("oc.renderFailedGeneric")}</div>
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  data-testid="oc-render-retry"
                  onClick={retryRender}
                  disabled={retrying}
                  className={`${btnPrimary} bg-gradient-to-br from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 disabled:opacity-60`}
                >
                  {retrying ? <Loader2 size={20} className="animate-spin" /> : <RefreshCw size={20} />}
                  {t("oc.retry")}
                </button>
                <button data-testid="oc-render-error-edit" onClick={() => onDismissToEditor && onDismissToEditor(projectId)} className="min-h-[48px] px-4 sm:px-5 text-base rounded-xl border-2 border-amber-500 bg-amber-50 text-amber-800 font-bold hover:bg-amber-100">{t("oc.editManually")}</button>
                <button data-testid="oc-render-error-back" onClick={onBackToChannel} className="min-h-[48px] px-4 sm:px-5 text-base rounded-xl border-2 border-slate-300 text-slate-700 font-bold hover:bg-slate-50">{t("oc.backToChannel")}</button>
              </div>
            </div>
          </div>
        </Alert>
      )}

      {/* ══ CADENA DE MONTAJE ══ Riel de 5 estaciones + un único viewport que narra en tiempo
          real la estación activa, con fundido cruzado al avanzar de una a otra. */}
      {showAssemblyLine && (
        <div className="space-y-5" data-testid="oc-assembly-line">
          <AssemblyRail stages={STAGES} activeIdx={stageIdx} />
          <AnimatePresence mode="wait">
            <motion.div key={stageIdx}
              initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -14 }}
              transition={{ duration: 0.35, ease: "easeOut" }}>
              {stageIdx === 0 && <ScriptStage t={t} msgIdx={msgIdx} scriptMsgs={scriptMsgs} elapsedSec={elapsedSec} />}
              {stageIdx === 1 && (
                segments.length > 0
                  ? <NarrationStage t={t} segments={segments} playingSeg={playingSeg} playSeg={playSeg} activeIdx={firstNarrPendingIdx} />
                  : <ScriptStage t={t} msgIdx={msgIdx} scriptMsgs={scriptMsgs} elapsedSec={elapsedSec} />
              )}
              {stageIdx === 2 && (
                <MediaStage t={t} sourceLabel={sourceLabel} clips={clips} clipsByScene={clipsByScene}
                  activeIdx={firstUnfilledIdx} stepDetail={status.step_detail} />
              )}
              {stageIdx === 3 && <ComposingStage t={t} msgIdx={msgIdx} composingMsgs={composingMsgs} musicMood={musicMoodLabel} />}
              {stageIdx === 4 && <RenderStage t={t} liveRender={liveRender} clipsByScene={clipsByScene} />}
            </motion.div>
          </AnimatePresence>
        </div>
      )}

      {/* CIERRE: render completo → vídeo + miniatura + publicar en YouTube, sin salir de aquí */}
      {renderComplete && !showMaterialEdit && (
        <PublishStage t={t} project={project} out={liveRender.output || project?.render?.output} projectId={projectId}
          onRecreate={() => setShowMaterialEdit(true)} onCreateAnother={onCreateAnother} />
      )}

      {/* PREVIEW: awaiting_approval, o revisión local post-render (mismo panel, sin tocar la BD) */}
      {(isDone || (renderComplete && showMaterialEdit)) && (
        <>
          {isDone ? (
          <Card className="p-6 border-2 border-amber-400 bg-gradient-to-br from-amber-50 to-orange-50">
            <div className="flex items-start gap-4">
              <CheckCircle2 size={32} className="text-emerald-600 shrink-0 mt-1" />
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-slate-900">{t("oc.readyTitle")}</h2>
                <p className="text-slate-700 mt-1">
                  <Trans i18nKey="oc.readyBody" values={{
                      clips: clips.length,
                      sources: new Set(clips.map((c) => c.director_source).filter(Boolean)).size,
                      scenes: segments.length,
                    }} components={{ b: <b /> }} />
                </p>
                {musicMoodLabel && (
                  <div className="mt-2 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/70 border border-amber-300 text-sm font-semibold text-amber-900">
                    🎵 {t("oc.musicChosen", { mood: musicMoodLabel })} <span className="font-normal text-slate-500">{t("oc.musicChosenHint")}</span>
                  </div>
                )}
                <div className="flex flex-wrap gap-3 mt-4">
                  <button
                    data-testid="oneclick-approve"
                    onClick={approve}
                    disabled={approving || rejecting}
                    className={`${btnPrimary} bg-gradient-to-br from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 disabled:opacity-60`}
                  >
                    {approving ? <Loader2 size={20} className="animate-spin" /> : <PlayCircle size={20} />}
                    {t("oc.approveAndRender")}
                  </button>
                  <button
                    data-testid="oneclick-reject"
                    onClick={reject}
                    disabled={approving || rejecting}
                    className="min-h-[48px] px-4 sm:px-5 text-base rounded-xl border-2 border-slate-300 text-slate-700 font-bold hover:bg-white flex items-center justify-center gap-2 disabled:opacity-60"
                  >
                    {rejecting ? <Loader2 size={20} className="animate-spin" /> : <RefreshCw size={20} />}
                    {t("oc.regenerateBroll")}
                  </button>
                  <button
                    data-testid="oneclick-edit-manually"
                    onClick={() => onDismissToEditor && onDismissToEditor(projectId)}
                    className="min-h-[48px] px-4 sm:px-5 text-base rounded-xl text-slate-600 font-bold hover:bg-white/60 flex items-center justify-center gap-2"
                  >
                    {t("oc.editManually")}
                  </button>
                </div>
              </div>
            </div>
          </Card>
          ) : (
          <Card className="p-6 border-2 border-emerald-300 bg-gradient-to-br from-emerald-50 to-teal-50">
            <div className="flex items-start gap-4">
              <RefreshCw size={32} className="text-emerald-600 shrink-0 mt-1" />
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-slate-900">{t("oc.editMaterialTitle")}</h2>
                <p className="text-slate-700 mt-1">{t("oc.editMaterialBody")}</p>
                <div className="flex flex-wrap gap-3 mt-4">
                  <button
                    data-testid="oc-rerender-button"
                    onClick={rerenderVideo}
                    disabled={rerendering || regeneratingAll || fillingGaps}
                    className={`${btnPrimary} bg-gradient-to-br from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 disabled:opacity-60`}
                  >
                    {rerendering ? <Loader2 size={20} className="animate-spin" /> : <PlayCircle size={20} />}
                    {t("oc.rerenderVideo")}
                  </button>
                  <button
                    data-testid="oc-fill-broll-gaps"
                    onClick={fillBrollGaps}
                    disabled={rerendering || regeneratingAll || fillingGaps}
                    title={t("oc.fillGapsHint")}
                    className="min-h-[48px] px-4 sm:px-5 text-base rounded-xl border-2 border-blue-300 bg-blue-50 text-blue-900 font-bold hover:bg-blue-100 flex items-center justify-center gap-2 disabled:opacity-60"
                  >
                    {fillingGaps ? <Loader2 size={20} className="animate-spin" /> : <RefreshCw size={20} />}
                    {t("oc.fillGaps")}
                  </button>
                  <button
                    data-testid="oc-regenerate-all-broll"
                    onClick={regenerateAllBroll}
                    disabled={rerendering || regeneratingAll || fillingGaps}
                    className="min-h-[48px] px-4 sm:px-5 text-base rounded-xl border-2 border-amber-300 bg-amber-50 text-amber-900 font-bold hover:bg-amber-100 flex items-center justify-center gap-2 disabled:opacity-60"
                  >
                    {regeneratingAll ? <Loader2 size={20} className="animate-spin" /> : <RefreshCw size={20} />}
                    {t("oc.regenerateAllBroll")}
                  </button>
                  <button
                    data-testid="oc-cancel-material-edit"
                    onClick={() => setShowMaterialEdit(false)}
                    disabled={rerendering || regeneratingAll || fillingGaps}
                    className="min-h-[48px] px-4 sm:px-5 text-base rounded-xl border-2 border-slate-300 text-slate-700 font-bold hover:bg-white flex items-center justify-center gap-2 disabled:opacity-60"
                  >
                    {t("common.cancel")}
                  </button>
                </div>
              </div>
            </div>
          </Card>
          )}

          {/* GRID DE THUMBNAILS POR ESCENA */}
          <div className="space-y-6">
            {clipsByScene.map(({ seg, clips: sceneClips }) => (
              <Card key={seg.id} className="p-5">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 rounded-full bg-slate-900 text-white flex items-center justify-center font-bold text-sm">{seg.num}</div>
                  <div className="flex-1">
                    <div className="font-bold text-lg text-slate-900 leading-tight">{seg.title}</div>
                    <div className="text-sm text-slate-500">{t("oc.clipsCount", { count: sceneClips.length })} · {t("oc.narratedSeconds", { count: Math.round(seg.narration_duration || seg.duration || 0) })}</div>
                  </div>
                </div>
                {sceneClips.length === 0 ? (
                  <div className="text-center py-6 bg-amber-50 border-2 border-dashed border-amber-300 rounded-lg">
                    <AlertTriangle size={22} className="mx-auto text-amber-500 mb-1" />
                    <div className="text-sm text-amber-800 font-semibold">{t("oc.noMaterialTitle")}</div>
                    <div className="text-xs text-amber-700">{t("oc.noMaterialDesc")}</div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {sceneClips.map((c) => {
                      const src = c.director_source || "?";
                      const badge = sourceLabel(src);
                      const color = SOURCE_COLORS[src] || "bg-slate-100 text-slate-700";
                      return (
                        <div key={c.id}
                          className="relative rounded-lg overflow-hidden border-2 border-slate-200 text-left" data-testid={`oc-clip-${c.id}`}>
                          {/* Antes: toda la tarjeta era un <button> que reemplazaba al pulsar
                              cualquier punto, con un overlay de texto que aparecía solo en
                              hover — quitado (jul 2026, sobrecargaba y competía con el hover del
                              preview de vídeo). Ahora "reemplazar" es un botón EXPLÍCITO dentro
                              de ClipThumbnail (junto al de preview, mismo contenedor de
                              referencia) — un <div> normal no puede anidar un <button> real
                              (HTML inválido) así que este contenedor dejó de ser un <button>. */}
                          <ClipThumbnail c={c} t={t} playing={previewPlayingId === c.id} onTogglePreview={togglePreview} onReplace={openReplace} />
                          {c.director_query ? (
                            <TooltipProvider delayDuration={150}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className={`absolute top-1.5 left-1.5 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wide ${color} shadow-sm cursor-help`}>
                                    {badge}
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="max-w-[220px] bg-slate-900 text-white text-xs font-medium leading-snug px-2.5 py-1.5 rounded-lg shadow-2xl border border-white/10">
                                  {c.director_query}
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          ) : (
                            <div className={`absolute top-1.5 left-1.5 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wide ${color} shadow-sm`}>
                              {badge}
                            </div>
                          )}
                          {c.needs_review && (
                            <div
                              data-testid={`oc-clip-review-${c.id}`}
                              title={c.needs_review_reason === "unverified" ? t("oc.reviewUnverifiedTitle") : t("oc.reviewLicenseTitle")}
                              className="absolute top-1.5 right-1.5 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wide bg-amber-500 text-white shadow-sm">
                              <AlertTriangle size={11} /> {t("oc.review")}
                            </div>
                          )}
                          {c.director_query && (
                            <div className="px-2 py-1.5 bg-white text-xs text-slate-600 leading-tight truncate" title={c.director_query}>
                              {c.director_query}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </Card>
            ))}
          </div>
        </>
      )}

      {/* "Mi carpeta" a pantalla completa para reemplazar el clip pulsado: es un explorador
          denso (búsqueda, carpetas, grid de miniaturas) que se quedaba apretado en una modal
          centrada de max-w-4xl, sobre todo en móvil — mismo criterio que "insertar imagen" en
          Notion/Figma, que van a pantalla completa por el mismo motivo. Full-bleed en móvil,
          con margen tipo panel en pantallas grandes para que siga leyéndose como overlay. */}
      {replaceTarget && (() => {
        const { media: initMedia, source: initSource } = replaceSearchParams(replaceTarget);
        return (
          <ReplaceClipPanel onClose={closeReplace}>
            <MyFolder projectId={projectId} library={library} loading={loadingLib} onReload={loadLibrary}
              onUse={replaceClip} targetLabel={t("oc.replacingClip")}
              keywords={replaceTarget.director_query ? [replaceTarget.director_query] : []}
              autoOpenSearch replaceOnly
              initialMedia={initMedia} initialSource={initSource} />
          </ReplaceClipPanel>
        );
      })()}
    </div>
  );
}

function ReplaceClipPanel({ onClose, children }) {
  const { t } = useTranslation();
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);
  return (
    <div className="fixed inset-0 sm:inset-4 lg:inset-8 z-50 bg-white sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden"
      data-testid="replace-clip-modal">
      <div className="flex items-center justify-between gap-3 px-5 py-4 border-b-2 border-slate-100 shrink-0">
        <h2 className="text-xl font-bold text-slate-900">{t("oc.replaceClipTitle")}</h2>
        <button type="button" onClick={onClose} aria-label={t("common.close")} data-testid="close-replace-modal"
          className="min-h-[40px] min-w-[40px] rounded-full text-slate-500 hover:bg-slate-100 hover:text-slate-800 flex items-center justify-center shrink-0">
          <X size={22} />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto overflow-x-hidden vb-scroll p-4 sm:p-5">
        {children}
      </div>
    </div>
  );
}
